import React from 'react';

import SlideOver from '../../procurement/face/SlideOver';

import { EM_DASH, TimeReportDaysCell, TimeReportMoneyCell } from './timeReportFormat';

import { formatDateLong } from 'Helpers/dateFormat';

import Alert from 'CommonComponents/Alert';

/**
 * The entries behind one row, in a panel rather than on a page of their own —
 * the report is read by comparing rows, and navigating away to inspect one
 * loses the comparison.
 *
 * The footer restates what the entries add up to, so the panel proves it
 * reconciles to the row that opened it rather than asking to be trusted.
 */
export default function TimeReportEntriesDrawer({ row, entries, loading, error, currency, onClose }) {
    const isOpen = row !== null;

    const days = (entries || []).reduce((carry, entry) => carry + Number(entry.days || 0), 0);
    const billedValue = (entries || []).reduce((carry, entry) => carry + Number(entry.billed_value || 0), 0);

    return (
        <SlideOver
            title={row ? row.label : ''}
            subtitle={row ? $t('{{count}} entries behind this row', { count: (entries || []).length }) : null}
            isOpen={isOpen}
            onClose={onClose}
        >
            {error && <Alert type="error">{error}</Alert>}

            {loading && <p className="tw-text-sm tw-text-charcoal-75 tw-mb-0">{$t('Loading')}…</p>}

            {!loading && !error && (entries || []).length === 0 && (
                <p className="tw-text-sm tw-text-charcoal-75 tw-mb-0">{$t('No time entries behind this row.')}</p>
            )}

            {!loading && !error && (entries || []).length > 0 && (
                <div className="inbud-table_wrapper">
                    <table className="inbud-table">
                        <thead>
                            <tr>
                                <th className="tw-text-start">{$t('Date')}</th>
                                <th className="tw-text-start">{$t('Person')}</th>
                                <th className="tw-text-start">{$t('Note')}</th>
                                <th className="tw-text-end">{$t('Days')}</th>
                                <th className="tw-text-end">Charged</th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map((entry, index) => (
                                <tr key={`${entry.date}-${entry.person}-${index}`}>
                                    {/* String surgery on the ISO date, not a Date:
                                        a work date carries no time or zone, and
                                        parsing it would shift the day west of UTC. */}
                                    <td className="inbud-date-cell">{formatDateLong(entry.date)}</td>
                                    <td>{entry.person}</td>
                                    <td className="tw-text-charcoal-75">{entry.note || EM_DASH}</td>
                                    <td className="inbud-numeric-cell">
                                        <TimeReportDaysCell value={entry.days} />
                                    </td>
                                    <td className="inbud-numeric-cell">
                                        <TimeReportMoneyCell value={entry.billed_value} currency={currency} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td className="tw-text-start" colSpan={3}>
                                    {$t('Total')}
                                </td>
                                <td className="inbud-numeric-cell">
                                    <TimeReportDaysCell value={days} />
                                </td>
                                <td className="inbud-numeric-cell">
                                    <TimeReportMoneyCell value={billedValue} currency={currency} />
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </SlideOver>
    );
}
